import React from 'react';

import { SequenceRegion } from './SequenceRegion';
import type { RegionNumbering, Shm } from '../../../generated/generatedOpenAPI';
import { REGION_ORDER } from '../../constants/regions';
import type { SequenceData } from '../../types';

/**
 * SequenceWithRegions Component
 *
 * Renders a full sequence broken down into its annotated regions (FWR/CDR)
 * with liability highlighting, SHM highlighting and search functionality
 */

const s = {
  empty: 'font-mono text-xs whitespace-nowrap text-gray-400',
  row: 'flex font-mono text-xs items-stretch',
  divider: 'w-px bg-slate-400 flex-shrink-0 mx-[2px]',
  regionWrapper: 'flex flex-col',
  regionRuler: 'flex justify-between font-mono text-[9px] leading-none text-gray-400 px-[1px]',
} as const;

// Standalone const so the fallback below can reference it without repeating the literal
// (a plain string stays non-optional under `noUncheckedIndexedAccess`, unlike CDR_BG.cdr3).
const CDR3_BG = 'bg-[#ffe6bd]';

const CDR_BG: Record<string, string> = {
  cdr1: 'bg-[#c2ecdf]',
  cdr2: 'bg-[#d9c9ff]',
  cdr3: CDR3_BG,
};

interface SequenceWithRegionsProps {
  sequence: SequenceData;
  showFWR: boolean;
  highlightedRegions: string[];
  onRegionClick?: (region: string | null) => void;
  searchQuery?: string;
  chainKey?: 'heavy' | 'light';
  showLiabilities?: boolean;
  showShms?: boolean;
  regionNumbers?: RegionNumbering[];
}

export const SequenceWithRegions = React.memo<SequenceWithRegionsProps>(
  ({ sequence, showFWR, highlightedRegions, onRegionClick, searchQuery, chainKey, showLiabilities = true, showShms = false, regionNumbers }) => {
    const chainData = chainKey === 'light' ? sequence.lightChain : chainKey === 'heavy' ? sequence.heavyChain : sequence.heavyChain || sequence.lightChain;

    const liabilityPositions = React.useMemo(() => {
      const posMap = new Map<number, string>();
      if (showLiabilities && chainData?.liabilities) {
        chainData.liabilities.forEach((liability) => {
          const residueLength = liability.residue.length;
          for (let i = 0; i < residueLength; i++) {
            posMap.set(liability.index + i, liability.severity);
          }
        });
      }
      return posMap;
    }, [chainData?.liabilities, showLiabilities]);

    const shmPositions = React.useMemo(() => {
      const posMap = new Map<number, Shm>();
      if (showShms && chainData?.shms) {
        chainData.shms.forEach((shm) => {
          posMap.set(shm.index, shm);
        });
      }
      return posMap;
    }, [chainData?.shms, showShms]);

    if (!chainData) {
      return <div className={s.empty}>No sequence data</div>;
    }

    const activeRegions = highlightedRegions?.length ? highlightedRegions : ['cdr3'];

    const visibleRegions: { key: string; isCDR: boolean; regionSeq: string; startPos: number; bgColor: string }[] = [];
    let pos = 0;
    for (const { key, isCDR } of REGION_ORDER) {
      const regionSeq = chainData.regions[key];
      if (!regionSeq) {
        continue;
      }
      const visible = isCDR ? activeRegions.includes(key) : showFWR && activeRegions.includes(key);
      if (visible) {
        visibleRegions.push({ key, isCDR, regionSeq, startPos: pos, bgColor: isCDR ? (CDR_BG[key] ?? CDR3_BG) : '' });
      }
      // Always advance pos for ALL regions (visible or not) so startPos matches full-sequence liability indices
      pos += regionSeq.length;
    }

    return (
      <div className={s.row}>
        {visibleRegions.map(({ key, isCDR, regionSeq, startPos, bgColor }, idx) => {
          const regionNum = regionNumbers?.find((r) => r.region === key);
          return (
            <React.Fragment key={key}>
              {idx > 0 && <div className={s.divider} />}
              <div className={s.regionWrapper}>
                {regionNum && (
                  <div className={s.regionRuler}>
                    <span>{regionNum.start}</span>
                    <span>{regionNum.end}</span>
                  </div>
                )}
                <SequenceRegion
                  regionSeq={regionSeq}
                  isCDR={isCDR}
                  isHighlighted
                  bgColor={bgColor}
                  regionKey={key}
                  onRegionClick={onRegionClick}
                  liabilityPositions={liabilityPositions}
                  shmPositions={shmPositions}
                  startPosition={startPos}
                  searchQuery={searchQuery}
                  fullSequence={chainData.sequence}
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  },
);

SequenceWithRegions.displayName = 'SequenceWithRegions';
